/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace gumbel
*/
var gumbel = {};

/**
* @name cdf
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/cdf}
*/
import cdf from './../../../../base/dists/gumbel/cdf';
setReadOnly( gumbel, 'cdf', cdf );

/**
* @name Gumbel
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/ctor}
*/
import Gumbel from './../../../../base/dists/gumbel/ctor';
setReadOnly( gumbel, 'Gumbel', Gumbel );

/**
* @name entropy
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/entropy}
*/
import entropy from './../../../../base/dists/gumbel/entropy';
setReadOnly( gumbel, 'entropy', entropy );

/**
* @name kurtosis
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/kurtosis}
*/
import kurtosis from './../../../../base/dists/gumbel/kurtosis';
setReadOnly( gumbel, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/logcdf}
*/
import logcdf from './../../../../base/dists/gumbel/logcdf';
setReadOnly( gumbel, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/logpdf}
*/
import logpdf from './../../../../base/dists/gumbel/logpdf';
setReadOnly( gumbel, 'logpdf', logpdf );

/**
* @name mean
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/mean}
*/
import mean from './../../../../base/dists/gumbel/mean';
setReadOnly( gumbel, 'mean', mean );

/**
* @name median
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/median}
*/
import median from './../../../../base/dists/gumbel/median';
setReadOnly( gumbel, 'median', median );

/**
* @name mgf
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/mgf}
*/
import mgf from './../../../../base/dists/gumbel/mgf';
setReadOnly( gumbel, 'mgf', mgf );

/**
* @name mode
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/mode}
*/
import mode from './../../../../base/dists/gumbel/mode';
setReadOnly( gumbel, 'mode', mode );

/**
* @name pdf
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/pdf}
*/
import pdf from './../../../../base/dists/gumbel/pdf';
setReadOnly( gumbel, 'pdf', pdf );

/**
* @name quantile
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/quantile}
*/
import quantile from './../../../../base/dists/gumbel/quantile';
setReadOnly( gumbel, 'quantile', quantile );

/**
* @name skewness
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/skewness}
*/
import skewness from './../../../../base/dists/gumbel/skewness';
setReadOnly( gumbel, 'skewness', skewness );

/**
* @name stdev
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/stdev}
*/
import stdev from './../../../../base/dists/gumbel/stdev';
setReadOnly( gumbel, 'stdev', stdev );

/**
* @name variance
* @memberof gumbel
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/gumbel/variance}
*/
import variance from './../../../../base/dists/gumbel/variance';
setReadOnly( gumbel, 'variance', variance );


// EXPORTS //

export default gumbel;
